/** The date-range choices shared by every sidebar panel's picker and the
 *  "Default date range" setting, so the two can never disagree.
 *
 *  Values pass straight through to the bridge's `dateRange` parameter, which
 *  reads them as calendar-day brackets ("0" today, "-1" yesterday, "1" today
 *  and yesterday, N the last N days plus today). "all" means no bound. */

export const DATE_RANGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "0",   label: "Today" },
  { value: "-1",  label: "Yesterday" },
  { value: "1",   label: "Last 2 days" },
  { value: "7",   label: "Last 7 days" },
  { value: "14",  label: "Last 14 days" },
  { value: "30",  label: "Last 30 days" },
  { value: "60",  label: "Last 60 days" },
  { value: "90",  label: "Last 90 days" },
  { value: "180", label: "Last 180 days" },
  { value: "360", label: "Last 360 days" },
  { value: "all", label: "All" },
];

export const DATE_RANGE_VALUES = new Set(DATE_RANGE_OPTIONS.map((o) => o.value));

/** Sidebar panels with their own date picker. */
export const DATE_RANGE_PANELS = ["section-outlook", "section-custom", "section-daily", "section-ai"] as const;

/** localStorage key of one panel's explicit choice. */
export function panelRangeStorageKey(panelId: string): string {
  return `mail.panel.${panelId}.dateRange`;
}

/** One date range for Outlook Folders, Triage, Custom Folders and AI
 *  Classifications (2026-10-02, Waqas): the range follows you between
 *  them. Daily Summary keeps its days, Search its own dates. Every view
 *  answers from the daemon's cache in milliseconds at any range, and the
 *  lists load as you scroll, so a long range no longer slows a view. */
export const MAIL_RANGE_KEY = "mail.dateRange";
const MAIL_RANGE_EVENT = "wov:mail-range";

/** The shared range: the stored choice, else the Outlook panel's old
 *  choice (the most used), else Last 2 days. */
export function readMailRange(): { value: string; explicit: boolean } {
  try {
    const v = window.localStorage.getItem(MAIL_RANGE_KEY);
    if (v && DATE_RANGE_VALUES.has(v)) return { value: v, explicit: true };
    const old = window.localStorage.getItem(panelRangeStorageKey("section-outlook"));
    if (old && DATE_RANGE_VALUES.has(old)) return { value: old, explicit: true };
  } catch {
    /* storage unavailable */
  }
  return { value: "1", explicit: false };
}

/** Chosen by the user (the title bar's View): kept, and it wins over the
 *  Default date range setting from then on. */
export function writeMailRange(value: string): void {
  if (!DATE_RANGE_VALUES.has(value)) return;
  try {
    window.localStorage.setItem(MAIL_RANGE_KEY, value);
  } catch {
    /* storage unavailable */
  }
  window.dispatchEvent(new CustomEvent(MAIL_RANGE_EVENT, { detail: { value, chosen: true } }));
}

/** Taken from the Default date range setting (not chosen): shown, not kept. */
export function announceMailRange(value: string): void {
  if (!DATE_RANGE_VALUES.has(value)) return;
  window.dispatchEvent(new CustomEvent(MAIL_RANGE_EVENT, { detail: { value, chosen: false } }));
}

export function onMailRangeChange(fn: (value: string, chosen: boolean) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent<{ value: string; chosen: boolean }>).detail;
    fn(d.value, d.chosen);
  };
  window.addEventListener(MAIL_RANGE_EVENT, h);
  return () => window.removeEventListener(MAIL_RANGE_EVENT, h);
}

export function rangeLabel(value: string): string {
  return DATE_RANGE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}
